import { gql, useLazyQuery, useMutation } from "@apollo/client";

import type {
  ICart,
  ICartBuyerIdentityUpdateMutation,
  ICartCreateInputMutation,
  ICartCreateMutation,
  ICartDiscountCodesUpdateMutation,
  ICartLinesAddMutation,
  ICartLinesRemoveMutation,
  ICartLinesUpdateMutation,
} from "@repo/shopify/queries/cart-gql";
import {
  cartBuyerIdentityUpdateMutation,
  cartCreateInputMutation,
  cartCreateMutation,
  cartDeferQuery,
  cartDiscountCodesUpdateMutation,
  cartLinesAddMutation,
  cartLinesRemoveMutation,
  cartLinesUpdateMutation,
  cartQuery,
} from "@repo/shopify/queries/cart-gql";
import { checkCartExpired } from "@repo/shopify/utils/cart";

import { NOTIFICATIONS } from "@const/notifications";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function useCartQuery() {
  const { cart, updateCart } = useCartStore();
  const { openToast, openCart } = useLayoutStore();
  const { customer, token, logout } = useCustomerStore();

  /** 카트 조회 */
  const [getCart, { loading: getCartLoading }] = useLazyQuery<ICart>(gql(cartQuery.query), {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.cart) updateCart(data.cart);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 체크아웃 INTL 카트(cart @defer) 조회 - deliveryGroups */
  const [getCartDefer, { loading: getCartDeferLoading, data: cartDeferData }] = useLazyQuery<ICart>(gql(cartDeferQuery.query), {
    fetchPolicy: "no-cache",
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 최초 생성 */
  const [createCart] = useMutation<ICartCreateMutation>(gql(cartCreateMutation.query), {
    onCompleted: (data: ICartCreateMutation) => {
      if (data.cartCreate.cart) {
        updateCart(data.cartCreate.cart);
      } else {
        void getCart({ variables: { id: cart.id } });
      }
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 생성 */
  const [createCartInput] = useMutation<ICartCreateInputMutation>(gql(cartCreateInputMutation.query), {
    onCompleted: (data: ICartCreateMutation, options) => {
      const userErrors = data.cartCreate?.userErrors || [];
      const isInvalidToken = userErrors.some((err) => err.code === "INVALID" && err.field?.includes("customerAccessToken"));

      if (isInvalidToken) {
        const { lines } = (options?.variables as { input: { lines: { merchandiseId: string; quantity: number }[] } })?.input ?? {};

        // jp(CAA)는 classic customerAccessToken과 별개 토큰 체계라, 이 필드가 거절됐다고 해서 "classic 세션이 끝났다"는 뜻이 아님.
        // store에 caaToken이 남아있다면(= getFreshCaaToken 단계에서 이미 죽은 세션으로 판정되어 로그아웃되지 않았다면)
        // CAA/Hiko 세션은 여전히 유효할 가능성이 높으므로, 로그아웃 없이 buyerIdentity만 빼고 재시도함
        // 연결은 App.tsx의 buyerIdentity 동기화 effect가 다음 렌더에서 getFreshCaaToken()으로 다시 시도함
        if (useCustomerStore.getState().caaToken) {
          void createCartInput({ variables: { input: { lines } } });
          return;
        }

        openToast(NOTIFICATIONS.WARNING_END_SESSION);
        logout();
        void createCartInput({ variables: { input: { lines } } });
        return;
      }

      if (data.cartCreate.cart) {
        updateCart(data.cartCreate.cart);
      } else {
        void getCart({ variables: { id: cart.id } });
      }
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 아이템 수량 업데이트 */
  const [updateCartLine, { loading: updateCartLineLoading }] = useMutation<ICartLinesUpdateMutation>(gql(cartLinesUpdateMutation.query), {
    onCompleted: (data, options) => {
      const userErrors = data?.cartLinesUpdate?.userErrors || [];
      if (userErrors.length > 0) {
        const isCartExpired = checkCartExpired({
          userErrors,
          cart,
          customer,
          inputLines: options?.variables?.lines,
          type: "update",
          accessToken: token.accessToken,
          createCartInput,
          onExpired: () => {
            openToast(NOTIFICATIONS.WARNING_CART_EXPIRE);
          },
        });

        if (isCartExpired) return;
        openToast(NOTIFICATIONS.ERROR_SERVER);
        return;
      }
      if (data.cartLinesUpdate.cart) updateCart(data.cartLinesUpdate.cart);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 아이템 제거 */
  const [removeCartLine, { loading: removeCartLineLoading }] = useMutation<ICartLinesRemoveMutation>(gql(cartLinesRemoveMutation.query), {
    onCompleted: (data, options) => {
      const userErrors = data?.cartLinesRemove?.userErrors || [];
      if (userErrors.length) {
        const isCartExpired = checkCartExpired({
          userErrors,
          cart,
          customer,
          inputLines: options?.variables?.lineIds,
          type: "remove",
          accessToken: token.accessToken,
          createCartInput,
          onExpired: () => {
            openToast(NOTIFICATIONS.WARNING_CART_EXPIRE);
          },
        });
        if (isCartExpired) return;
        openToast(NOTIFICATIONS.ERROR_SERVER);
        return;
      }
      if (data.cartLinesRemove.cart) updateCart(data.cartLinesRemove.cart);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 아이템 추가 */
  const [addCartLine, { loading: addCartLineLoading }] = useMutation<ICartLinesAddMutation>(gql(cartLinesAddMutation.query), {
    onCompleted: (data, options) => {
      const userErrors = data.cartLinesAdd?.userErrors || [];
      if (userErrors.length > 0) {
        const isCartExpired = checkCartExpired({
          userErrors,
          cart,
          customer,
          inputLines: options?.variables?.lines,
          type: "add",
          accessToken: token.accessToken,
          createCartInput,
          onRecreated: () => {
            openCart(true);
          },
        });
        if (isCartExpired) return;
        openToast(NOTIFICATIONS.ERROR_SERVER);
        return;
      }
      if (data.cartLinesAdd.cart) updateCart(data.cartLinesAdd.cart);
    },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  /** 카트 할인 코드 제어 */
  const [discountCart, { loading: discountCartLoading }] = useMutation<ICartDiscountCodesUpdateMutation>(
    gql(cartDiscountCodesUpdateMutation.query),
    {
      onCompleted: (data) => {
        if (data.cartDiscountCodesUpdate.cart) updateCart(data.cartDiscountCodesUpdate.cart);
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  const [setBuyerIdentity, { loading: setBuyerIdentityLoading }] = useMutation<ICartBuyerIdentityUpdateMutation>(
    gql(cartBuyerIdentityUpdateMutation.query),
    {
      onCompleted: (data) => {
        if (data.cartBuyerIdentityUpdate.cart) {
          updateCart(data.cartBuyerIdentityUpdate.cart);
        }
      },
      onError: (error) => {
        openToast(NOTIFICATIONS.ERROR_SERVER);
        throw new Error(error.message);
      },
    },
  );

  return {
    createCart,
    createCartInput,
    updateCartLine,
    updateCartLineLoading,
    removeCartLine,
    removeCartLineLoading,
    addCartLine,
    addCartLineLoading,
    getCart,
    getCartLoading,
    discountCart,
    discountCartLoading,
    setBuyerIdentity,
    setBuyerIdentityLoading,
    getCartDefer,
    cartDeferData,
    getCartDeferLoading,
  };
}
